import { Text } from '@macarongfactory/web-ui'

import HeroCars from './HeroCars'

import type { ReactNode } from 'react'

/** 히어로 배경 그라데이션 */
const DESKTOP_GRADIENT =
  'linear-gradient(169.45deg, rgba(0, 175, 255, 0) 48.835%, rgba(0, 175, 255, 0.1) 82.29%), #ffffff'
const MOBILE_GRADIENT =
  'linear-gradient(150.46deg, rgba(0, 175, 255, 0) 48.835%, rgba(0, 175, 255, 0.1) 82.29%), #ffffff'

interface IRankSummaryCardProps {
  /** 데스크탑: 세그먼트는 LNB로 선택(정적 라벨) / 모바일: 세그먼트 드롭다운 */
  variant: 'desktop' | 'mobile'
  /** 세그먼트명 (예: '국내 소형') */
  segment: string
  /** 탭 타이틀 (예: '오너 만족도 랭킹') */
  title: string
  /** 설명문 (데스크탑 전용, 줄바꿈 포함 가능) */
  description?: string
  /** 데이터 기준 문구 (예: '2026년 7월 1일 마이클 데이터 기준') */
  referenceNote: string
  /** 히어로 상위 3개 차종 이미지 */
  heroImageUrls: string[]
  /** 랭킹 데이터 존재 여부 — 없으면 모바일 히어로 이미지 자리를 제거한다 */
  hasRanking?: boolean
  /** 유종 선택기 (RankSelectSheet chip). 유류비 탭은 선택 UI가 없어 생략한다. */
  fuelSelector?: ReactNode
  /** 세그먼트 선택기 (모바일, RankSelectSheet underline) */
  segmentSelector?: ReactNode
  /** 차급·유종 선택 영역 노출 여부 — 유류비 탭은 false(타이틀만 노출) */
  showSegment?: boolean
}

const RankSummaryCard = ({
  variant,
  segment,
  title,
  description,
  referenceNote,
  heroImageUrls,
  hasRanking = true,
  fuelSelector,
  segmentSelector,
  showSegment = true,
}: IRankSummaryCardProps) => {
  const isMobile = variant === 'mobile'

  const dateNote = (
    <Text
      variant='body-2-medium'
      color='text-foreground-neutral-assistive'
      className='block w-full'
    >
      {referenceNote}
    </Text>
  )

  if (isMobile) {
    return (
      <section
        className='relative flex flex-col items-center gap-dimension-x4 px-dimension-x5 pb-dimension-x4 pt-dimension-x8'
        style={{ background: MOBILE_GRADIENT }}
      >
        <div className='flex w-full flex-col items-start gap-dimension-x1'>
          {showSegment ? (
            <div className='flex w-full items-center justify-between gap-dimension-x2'>
              {segmentSelector ?? (
                <Text
                  variant='display-1-bold'
                  color='text-foreground-brand-strong'
                >
                  {segment}
                </Text>
              )}
              {fuelSelector}
            </div>
          ) : null}
          <Text
            variant='display-1-bold'
            color='text-foreground-neutral-strong'
            className='block w-full'
          >
            {title}
          </Text>
        </div>

        {hasRanking ? (
          <div aria-hidden className='aspect-[226/127] w-full'>
            <HeroCars imageUrls={heroImageUrls} />
          </div>
        ) : null}

        {dateNote}
      </section>
    )
  }

  // desktop
  return (
    <section
      className='relative flex flex-col items-end gap-dimension-x4 rounded-dimension-x4 border border-stroke-brand-strong p-dimension-x8'
      style={{ background: DESKTOP_GRADIENT }}
    >
      <div className='flex w-full items-start gap-dimension-x2'>
        <div className='flex min-w-px flex-1 flex-col items-start gap-dimension-x2'>
          <div className='w-full'>
            {showSegment ? (
              <Text
                variant='display-1-bold'
                color='text-foreground-brand-strong'
                className='block'
              >
                {segment}
              </Text>
            ) : null}
            <Text
              variant='display-1-bold'
              color='text-foreground-neutral-strong'
              className='block'
            >
              {title}
            </Text>
          </div>
          {description ? (
            <Text
              variant='body-1-medium'
              color='text-foreground-neutral-alternative'
              className='block w-full whitespace-pre-line'
            >
              {description}
            </Text>
          ) : null}
        </div>
        {fuelSelector}
      </div>

      {/* 기준 문구는 우측 히어로 차량(226px) 영역을 피해 줄바꿈되도록 여백을 예약한다
          (Figma 8624:166885 — 텍스트 컬럼 flex-1 + 차량 226px 구조와 동일 결과) */}
      <div className='w-full pr-[240px]'>{dateNote}</div>

      <div
        aria-hidden
        className='pointer-events-none absolute bottom-[31px] right-[31px] h-[127px] w-[226px] overflow-hidden'
      >
        <HeroCars imageUrls={heroImageUrls} />
      </div>
    </section>
  )
}

export default RankSummaryCard
